import type { BlockDefinition, LinkButtonProps } from '../types';
import { ensureHttps, escapeHtml, maybeTokenize, safeUrl } from '../html';

export const linkButton: BlockDefinition<LinkButtonProps> = {
  type: 'link-button',
  meta: {
    label: 'Link button',
    icon: '🔘',
    category: 'buttons',
    description: 'A tappable call-to-action that opens a URL.',
  },
  defaults: () => ({
    label: 'Open',
    url: 'https://',
    variant: 'solid',
    newTab: true,
    icon: '',
    iconBg: 'primary',
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<LinkButtonProps>;
    return {
      label: typeof i.label === 'string' ? i.label.slice(0, 120) : '',
      url: typeof i.url === 'string' ? i.url.slice(0, 2000) : '',
      variant:
        i.variant === 'outline' ? 'outline' : i.variant === 'glass' ? 'glass' : 'solid',
      newTab: i.newTab !== false,
      icon: typeof i.icon === 'string' ? i.icon.slice(0, 8) : '',
      iconBg: typeof i.iconBg === 'string' ? i.iconBg.slice(0, 32) : 'primary',
    };
  },
  render(props, _theme, ctx) {
    const href = maybeTokenize(safeUrl(ensureHttps(props.url)), ctx?.tokenizeUrl);
    // rel="noopener noreferrer" on EVERY outbound link, regardless of
    // newTab. noopener is technically only meaningful for `_blank` (no
    // opener exists on same-tab nav), but noreferrer DOES strip the
    // Referer header on same-tab clicks too — which prevents the
    // destination from seeing that the traffic came from
    // go.poof.bio/<slug>. The page-wide referrer-policy meta + HTTP
    // header set this already, but per-link rel attrs are belt+braces
    // and override any same-origin overrides downstream might pull.
    const rel = 'rel="noopener noreferrer"';
    const target = props.newTab ? `target="_blank" ${rel}` : rel;
    const hasIcon = !!props.icon;

    // Variant-specific surface treatment. Glass uses backdrop-filter for
    // the frosted look on a hero photo; falls back gracefully to a darker
    // tint on browsers that don't support backdrop-filter.
    let surface: string;
    let labelColor = 'inherit';
    if (props.variant === 'glass') {
      surface =
        'background:rgba(255,255,255,0.07);' +
        'backdrop-filter:blur(14px) saturate(160%);' +
        '-webkit-backdrop-filter:blur(14px) saturate(160%);' +
        'border:1px solid rgba(255,255,255,0.10);';
      labelColor = 'var(--text)';
    } else if (props.variant === 'outline') {
      surface = 'background:transparent;border:2px solid var(--primary);';
      labelColor = 'var(--primary)';
    } else {
      // solid
      surface = 'background:var(--primary);border:2px solid var(--primary);';
      labelColor = '#fff';
    }

    // Icon-style buttons get the horizontal card layout (icon left, label
    // centered) like the DESIGN.md hero example. Plain buttons get a
    // simple centered label.
    if (hasIcon) {
      const iconBg = resolveIconBg(props.iconBg);
      // The padding-right on the label balances the icon's width so the
      // visual center of the label stays centered in the button.
      return `<div class="pb-block pb-link-button">
  <a href="${href}" ${target} style="display:flex;align-items:center;width:100%;min-height:64px;padding:8px;gap:0;border-radius:var(--radius-btn);${surface}font-size:15px;font-weight:600;text-decoration:none;transition:transform 0.1s,filter 0.15s,background 0.15s;color:${labelColor};" onmousedown="this.style.transform='scale(0.99)'" onmouseup="this.style.transform=''">
    <span aria-hidden="true" style="display:flex;align-items:center;justify-content:center;width:48px;height:48px;border-radius:9999px;background:${iconBg.bg};color:${iconBg.fg};font-size:22px;flex-shrink:0;">${escapeHtml(props.icon || '')}</span>
    <span style="flex:1;text-align:center;padding-right:56px;">${escapeHtml(props.label)}</span>
  </a>
</div>`;
    }

    // Plain (no-icon) button — classic centered label.
    return `<div class="pb-block pb-link-button">
  <a href="${href}" ${target} style="display:block;width:100%;padding:14px 20px;border-radius:var(--radius-btn);${surface}color:${labelColor};font-size:16px;font-weight:600;text-align:center;text-decoration:none;transition:transform 0.1s,filter 0.15s,background 0.15s;" onmousedown="this.style.transform='scale(0.99)'" onmouseup="this.style.transform=''">${escapeHtml(props.label)}</a>
</div>`;
  },
};

/** Map an iconBg token / hex to (bg, fg) CSS values. */
function resolveIconBg(token: string | undefined): { bg: string; fg: string } {
  if (!token || token === 'primary') return { bg: 'var(--primary)', fg: '#fff' };
  if (token === 'secondary') return { bg: 'rgba(255,255,255,0.08)', fg: 'var(--text)' };
  if (token === 'transparent') return { bg: 'transparent', fg: 'var(--text)' };
  // Hex value — assume light-text on whatever color the customer picked.
  // For pure white we'd want dark text; for now keep it simple.
  const hex = token.replace(/^#/, '');
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return { bg: '#' + hex, fg: '#fff' };
  }
  return { bg: 'var(--primary)', fg: '#fff' };
}
